import {
    Back,
    Container,
    ContentContainer,
    PageTitle,
    StyledTextField,
    palette,
} from '@src/core/styles';
import type {FC} from 'react';
import {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {FieldGroup} from '../login-page/login-page.styles';
import {FormContainer} from '../route-management-page/components/form-component/form-components.styles';
import {Button} from '@src/components';
import {ReactComponent as Arrow} from '@src/core/icons/arrow.svg';
import {ReactComponent as Check} from '@src/core/icons/check.svg';
import {useNavigate} from 'react-router-dom';
import {joiResolver} from '@hookform/resolvers/joi';
import {recoveryPasswordSchema} from '@src/core/schemas';
import type {
    RecoveryPasswordErrorResponse,
    RecoveryPasswordRequestParams,
    RecoveryPasswordrtErrors,
} from '@src/core/types';
import {Authentication} from '@src/core/services';
import {useMutation} from '@tanstack/react-query';
import {enqueueSnackbar} from 'notistack';
import {AxiosError} from 'axios';
import {Captcha} from "@src/components/captcha";

export const RecoveryPassword: FC = () => {
    const navigate = useNavigate();
    const [errorsRequest, setErrorsRequest] =
        useState<RecoveryPasswordrtErrors>();

    const [captchaState, setCaptchaState] = useState<boolean>(false);
    const captchaValidation = (value: any) => {
        setCaptchaState(value);

        return value;
    }

    const {mutate} = useMutation(Authentication.recoveryPassword, {
        onSuccess: (response) => {
            enqueueSnackbar(`${response.data.message}`, {
                variant: 'reportComplete',
                className: 'success',
                preventDuplicate: true,
            });
        },
        onError: (error: AxiosError<RecoveryPasswordErrorResponse>) => {
            setErrorsRequest(error.response?.data.errors);
        },
    });

    const {
        handleSubmit,
        control,
        formState: {errors, isValid},
    } = useForm<RecoveryPasswordRequestParams>({
        mode: 'onChange',
        resolver: joiResolver(recoveryPasswordSchema),
        defaultValues: {
            email: '',
        },
    });

    const fullValidation = isValid && captchaState;

    const backHandler = () => {
        navigate(-1);
    };

    const onSubmit = (data: RecoveryPasswordRequestParams) => {
        mutate(data);
    };

    const changeHandler = () => {
        setErrorsRequest(undefined);
    };

    return (
        <Container>
            <ContentContainer>
                <FormContainer>
                    <Back onClick={backHandler}>
                        <Arrow/>
                    </Back>
                    <PageTitle>Восстановление пароля</PageTitle>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <FieldGroup>
                            <Controller
                                control={control}
                                name="email"
                                render={({field: {value, onBlur, onChange}}) => (
                                    <StyledTextField
                                        onBlur={onBlur}
                                        variant="outlined"
                                        label="Электронная почта"
                                        placeholder="Электронная почта"
                                        autoComplete="on"
                                        InputLabelProps={{
                                            sx: {
                                                '&.Mui-focused': {
                                                    color: `${palette.colors.green_base}`,
                                                },
                                                '&.Mui-error': {color: `${palette.colors.error}`},
                                            },
                                        }}
                                        value={value}
                                        error={!!errors?.email?.message || !!errorsRequest?.email}
                                        helperText={errors?.email?.message || errorsRequest?.email}
                                        onChange={(e) => {
                                            changeHandler();
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                        </FieldGroup>
                        <Captcha CaptchaState={captchaValidation}/>

                        <Button
                            text="Восстановить пароль"
                            type="submit"
                            icon={<Check/>}
                            borderradius="L"
                            variant="contained"
                            disabled={!fullValidation}
                        />
                    </form>
                </FormContainer>
            </ContentContainer>
        </Container>
    );
};
